import * as React from "react";

// Distance from the scroll container's edge (px) at which auto-scrolling kicks in
const EDGE_THRESHOLD = 80;
// Maximum scroll distance per animation frame (px), reached when the pointer is at/past the edge
const MAX_SCROLL_STEP = 20;

const getScrollContainer = (node: HTMLElement | null): HTMLElement => {
  let el = node?.parentElement ?? null;
  while (el && el !== document.body && el !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight) return el;
    el = el.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
};

const getScrollStep = (distanceToEdge: number) => {
  if (distanceToEdge >= EDGE_THRESHOLD) return 0;
  const ratio = Math.min(1, (EDGE_THRESHOLD - distanceToEdge) / EDGE_THRESHOLD);
  return Math.ceil(ratio * MAX_SCROLL_STEP);
};

/**
 * Scrolls the nearest scrollable ancestor (or the document) while a drag is held near its top/bottom edge.
 *
 * react-grid-layout ships its own edge scroller, but it only detects elements with `overflow: auto|scroll`,
 * so it does nothing when the page itself scrolls. After each scroll step the last mousemove is replayed so
 * react-draggable re-positions the dragged item under the (possibly stationary) cursor.
 */
const useDragAutoScroll = () => {
  const stateRef = React.useRef<{
    container: HTMLElement;
    clientX: number;
    clientY: number;
    isMouse: boolean;
    rafId: number | null;
  } | null>(null);

  const step = React.useCallback(() => {
    const state = stateRef.current;
    if (!state) return;

    const { container, clientY } = state;
    const isDocument = container === document.scrollingElement || container === document.documentElement;
    const top = isDocument ? 0 : container.getBoundingClientRect().top;
    const bottom = isDocument ? window.innerHeight : container.getBoundingClientRect().bottom;
    const maxScrollTop = container.scrollHeight - container.clientHeight;

    let dy = 0;
    if (container.scrollTop > 0) dy = -getScrollStep(clientY - top);
    if (dy === 0 && container.scrollTop < maxScrollTop) dy = getScrollStep(bottom - clientY);

    if (dy !== 0) {
      const previousScrollTop = container.scrollTop;
      container.scrollTop = previousScrollTop + dy;

      if (state.isMouse && container.scrollTop !== previousScrollTop) {
        document.dispatchEvent(
          new MouseEvent("mousemove", { clientX: state.clientX, clientY, bubbles: true, cancelable: true, view: window })
        );
      }
    }

    state.rafId = requestAnimationFrame(step);
  }, []);

  const stop = React.useCallback(() => {
    const state = stateRef.current;
    if (state?.rafId != null) cancelAnimationFrame(state.rafId);
    stateRef.current = null;
  }, []);

  const track = React.useCallback(
    (event: Event, element: HTMLElement | null) => {
      const point = "touches" in event ? (event as TouchEvent).touches[0] : (event as MouseEvent);
      if (!point) return;

      if (!stateRef.current) {
        stateRef.current = {
          container: getScrollContainer(element),
          clientX: point.clientX,
          clientY: point.clientY,
          isMouse: !("touches" in event),
          rafId: null,
        };
        stateRef.current.rafId = requestAnimationFrame(step);
        return;
      }

      stateRef.current.clientX = point.clientX;
      stateRef.current.clientY = point.clientY;
    },
    [step]
  );

  React.useEffect(() => stop, [stop]);

  return { track, stop };
};

export default useDragAutoScroll;
